import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  alpha,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import type { LegalAppearance } from './common/LegalAppearance';
import { publicPath } from './marketing/public/routes';

interface TermsOfServiceProps {
  onBack: () => void;
  language?: 'sv' | 'en';
  appearance?: LegalAppearance;
}

const TermsOfService: React.FC<TermsOfServiceProps> = ({ onBack, language = 'sv', appearance }) => {
  const brandColors = appearance || {
    primary: '#0EA5E9',
    text: '#0F172A',
    textSecondary: '#475569',
    background: '#F8FAFC',
  };

  const content = {
    sv: {
      title: 'Användarvillkor',
      subtitle: 'KlaraView AB',
      backButton: '← Tillbaka',
      updated: 'Senast uppdaterad: 27 september 2026',
      sections: [
        {
          title: '1. Tjänsten och uppdraget',
          paragraphs: [
            'KlaraView tillhandahåller tjänster och en digital plattform för bilddokumentation, analys och beslutsstöd för fastighetsägare och förvaltare.',
            'Vilka ytor som omfattas, vilken granskning som ingår, leveranser, tidplan och pris bestäms i överenskommelsen för det enskilda uppdraget. Om dessa användarvillkor och uppdragsavtalet skiljer sig åt gäller uppdragsavtalet, med förbehåll för tvingande lag.',
          ],
        },
        {
          title: '2. Dokumentation och professionell bedömning',
          paragraphs: [
            'Bilder, observationer och analysförslag ska förstås utifrån inspektionens omfattning och förutsättningar. Bildbaserad analys kan inte ensam fastställa alla skador, deras orsaker eller vilka åtgärder som behövs.',
            'Scan omfattar bilddokumentation samt AI-genererade prioriteringar och förslag på nästa steg, granskade av KlaraView-teamet. Denna granskning är inte en ingenjörsbedömning.',
            'När Expert ingår omfattar uppdraget även oberoende ingenjörsgranskning och tekniskt underlag inom överenskommen omfattning. Vilka undersökningar, bedömningar och rekommendationer som ingår framgår av uppdragsavtalet.',
          ],
        },
        {
          title: '3. Kundens material och användning av leveransen',
          paragraphs: [
            'Kunden behåller de rättigheter som kunden har till material som lämnas till KlaraView. Kunden ansvarar för att ha rätt att tillhandahålla materialet för uppdraget.',
            'Kunden får använda levererade bilder, rapporter och övriga underlag för det avtalade ändamålet. Detta omfattar delning med de personer och organisationer som behöver underlaget för ändamålet, exempelvis styrelse, förvaltare, konsulter och entreprenörer.',
            'När material delas ska dess sammanhang, angivna begränsningar och uppgifter om vem som gjort en bedömning följa med. Särskilda villkor för användning och överlämning anges i uppdragsavtalet.',
          ],
        },
        {
          title: '4. KlaraViews teknik',
          paragraphs: [
            'Rättigheterna till KlaraViews programvara, analysmodeller och plattformens utformning tillhör KlaraView eller dess licensgivare.',
            'Kundens rätt att använda levererat material innebär inte att rättigheter till den bakomliggande tekniken överlåts.',
          ],
        },
        {
          title: '5. Hantering av information',
          paragraphs: [
            'Kundens fastighets- och bilddata behandlas konfidentiellt och används för det avtalade uppdragets ändamål. Inspektionsdata lagras och behandlas inom EU/EES.',
            'Åtkomst, delning, lagringstid, export och hantering av material vid uppdragets avslut bestäms i överenskommelsen för uppdraget. Tillgång till plattformen innebär inte ett löfte om obegränsad lagring.',
            'Information om behandling av personuppgifter och registrerades rättigheter finns i KlaraViews integritetspolicy.',
          ],
          link: { label: 'Integritetspolicy', href: publicPath('privacy', 'sv') },
        },
        {
          title: '6. Ansvar',
          paragraphs: [
            'KlaraView ansvarar för sina åtaganden enligt uppdragsavtalet och tillämplig lag. Kunden ansvarar för sina beslut om förvaltning och åtgärder samt för att underlaget används inom sitt avsedda ändamål.',
            'Kundens ansvar för sina beslut befriar inte KlaraView från ansvar för den avtalade leveransen. Ansvar för professionella bedömningar som ingår i uppdraget regleras i uppdragsavtalet och enligt tillämplig lag.',
          ],
        },
        {
          title: '7. Förändringar och kontakt',
          paragraphs: [
            'KlaraView kan vidareutveckla plattformen och uppdatera dessa användarvillkor. En uppdatering av villkoren på webbplatsen ändrar inte i sig ett redan ingånget uppdragsavtal.',
            'För frågor om tjänsten, materialet eller villkoren, kontakta oss.',
          ],
          link: { label: 'info@klaraview.se', href: 'mailto:info@klaraview.se' },
        },
      ],
    },
    en: {
      title: 'Terms of Service',
      subtitle: 'KlaraView AB',
      backButton: '← Back',
      updated: 'Last updated: 27 September 2026',
      sections: [
        {
          title: '1. The service and the assignment',
          paragraphs: [
            'KlaraView provides services and a digital platform for image documentation, analysis and decision support for property owners and managers.',
            'The surfaces covered, the review included, deliverables, schedule and price are set out in the agreement for each assignment. If these terms of service and the assignment agreement differ, the assignment agreement takes precedence, subject to mandatory law.',
          ],
        },
        {
          title: '2. Documentation and professional assessment',
          paragraphs: [
            'Images, observations and analysis suggestions must be understood in the context of the inspection scope and conditions. Image-based analysis alone cannot establish all defects, their causes or the measures needed.',
            'Scan includes image documentation and AI-generated priorities and suggested next steps, reviewed by the KlaraView team. This review is not an engineering assessment.',
            'When Expert is included, the assignment also covers independent engineering review and technical documentation within the agreed scope. The investigations, assessments and recommendations included are set out in the assignment agreement.',
          ],
        },
        {
          title: '3. Customer material and use of deliverables',
          paragraphs: [
            'The customer retains the rights they hold in material provided to KlaraView. The customer is responsible for having the right to provide the material for the assignment.',
            'The customer may use delivered images, reports and other documentation for the agreed purpose. This includes sharing with people and organisations that need the documentation for that purpose, such as the board, property managers, consultants and contractors.',
            'When material is shared, its context, stated limitations and information about who made an assessment must accompany it. Specific conditions for use and handover are set out in the assignment agreement.',
          ],
        },
        {
          title: '4. KlaraView technology',
          paragraphs: [
            'Rights to KlaraView software, analysis models and platform design belong to KlaraView or its licensors.',
            'The customer’s right to use delivered material does not transfer rights to the underlying technology.',
          ],
        },
        {
          title: '5. Information handling',
          paragraphs: [
            'Customer property and image data is treated confidentially and used for the agreed assignment purposes. Inspection data is stored and processed within the EU/EEA.',
            'Access, sharing, retention, export and handling of material at the end of the assignment are set out in the assignment agreement. Access to the platform does not constitute a promise of unlimited storage.',
            'Information about the processing of personal data and the rights of data subjects is available in KlaraView’s privacy policy.',
          ],
          link: { label: 'Privacy policy', href: publicPath('privacy', 'en') },
        },
        {
          title: '6. Responsibility',
          paragraphs: [
            'KlaraView is responsible for its commitments under the assignment agreement and applicable law. The customer is responsible for decisions about property management and measures, and for using the documentation for its intended purpose.',
            'The customer’s responsibility for their decisions does not relieve KlaraView of responsibility for the agreed deliverables. Responsibility for professional assessments included in the assignment is governed by the assignment agreement and applicable law.',
          ],
        },
        {
          title: '7. Changes and contact',
          paragraphs: [
            'KlaraView may develop the platform and update these terms of service. An update to the terms on the website does not, in itself, amend an assignment agreement already entered into.',
            'For questions about the service, material or terms, contact us.',
          ],
          link: { label: 'info@klaraview.se', href: 'mailto:info@klaraview.se' },
        },
      ],
    },
  };

  const t = content[language];

  return (
    <Box sx={{ bgcolor: brandColors.background, minHeight: '100vh', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="md">
        <Button
          onClick={onBack}
          startIcon={<ArrowBack />}
          sx={{
            mb: 4,
            color: brandColors.primary,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {t.backButton}
        </Button>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: appearance ? 0 : 3,
            border: `1px solid ${alpha(brandColors.text, 0.08)}`,
          }}
        >
          <Box sx={{ mb: 4 }}>
            {appearance ? <Box sx={{ mb: 3 }}>{appearance.logo}</Box> : <img
              src="/klaraview-logo-old.svg"
              alt="KlaraView"
              style={{ width: '160px', height: 'auto', marginBottom: '16px' }}
            />}
            <Typography
              variant="h3"
              component={appearance ? 'h1' : 'h3'}
              sx={{
                fontWeight: 700,
                color: brandColors.text,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                mb: 1,
              }}
            >
              {t.title}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ color: brandColors.textSecondary }}
            >
              {t.subtitle}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {t.sections.map(section => (
              <Box key={section.title}>
                <Typography
                  variant="h6"
                  component={appearance ? 'h2' : 'h4'}
                  sx={{ color: brandColors.text, fontWeight: 700, mb: 2 }}
                >
                  {section.title}
                </Typography>
                {section.paragraphs.map(paragraph => (
                  <Typography key={paragraph} sx={{ color: brandColors.textSecondary, lineHeight: 1.8, fontSize: '1.05rem', mb: 2 }}>
                    {paragraph}
                  </Typography>
                ))}
                {section.link && <Box component="a" href={section.link.href} sx={{
                  color: brandColors.primary, display: 'inline-flex', alignItems: 'center', minHeight: 44,
                  textUnderlineOffset: '4px',
                  '&:hover': { textDecorationThickness: '2px' },
                  '&:focus-visible': { outline: `3px solid ${brandColors.primary}`, outlineOffset: 4 },
                }}>
                  {section.link.label}
                </Box>}
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 6, pt: 4, borderTop: `1px solid ${alpha(brandColors.text, 0.08)}` }}>
            <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
              {t.updated}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfService;
